import { ApiError } from '../core/network/api';
import { CHARACTER_ROSTER, type CharacterClassId } from './config/character-roster';
import { classStepMarkup, talentOptionMarkup, abilityStepMarkup, classDisplayName, WEAPON_IDENTITY } from './ui/class-picker';
import { createCharacter, allocateTalent, getTalentTree, type TalentNodeRow } from './net/character-api';
import { iconMarkup, wireIconFallbacks } from './ui/icon';
import { talentCopy } from './config/talent-copy';
import { N_, t } from './i18n/i18n';
import { serverText } from './config/served-text';

/**
 * design/gdd/character-creation.md Core Rule #3 — the guided, WoW-style
 * multi-step flow: pick class -> review ability & class comparison -> review
 * stat profile (read-only) -> spend the one free talent point -> name and
 * confirm. Self-contained overlay, no dependency on sconce-hub-bootstrap.ts's
 * own DOM beyond being mounted into it.
 *
 * The cosmetic step was removed on 2026-09-01 at the user's request: "Remove
 * cosmetics from the hero creation process. This shouldn't be there." It asked
 * a player to choose a skin before they had one — a brand-new account owns
 * nothing, so the step's own copy told most people it had nothing to offer and
 * to skip it. Equipping still lives where it belongs, on the hub's Cosmetics
 * screen, against a character that already exists.
 */

type StepId = 'class' | 'abilities' | 'stats' | 'talent' | 'name';
const STEPS: StepId[] = ['class', 'abilities', 'stats', 'talent', 'name'];

interface AbilityInfo {
  /** The backend ability id, so this screen can show the same plate the action bar will. */
  id: string;
  name: string;
  blurb: string;
}

const ABILITY_INFO: Record<CharacterClassId, AbilityInfo> = {
  rogue: { id: 'vanish', name: N_('Vanish'), blurb: N_('Suppresses your noise for a few seconds — the escape hatch for a fight or a noise mistake gone wrong.') },
  knight: { id: 'brace', name: N_('Brace'), blurb: N_('Refuses to be staggered for a few seconds — eat the hit and keep fighting, the tank’s answer to forced group fights.') },
  barbarian: { id: 'warcry', name: N_('Warcry'), blurb: N_('An instant fear pulse against nearby monsters — area intimidation against the dungeon’s threats, never other players.') },
  // Role-based matchmaking amendment (2026-08-24) — the roster's HEALER.
  druid: { id: 'mend', name: N_('Mend'), blurb: N_('Restores health to a wounded ally — the only way a group keeps its footing through a long fight instead of retreating from it.') },
};

/** The table above, in the interface language — built at render time, never at load. */
function abilityInfoTranslated(): Record<CharacterClassId, AbilityInfo> {
  const out = {} as Record<CharacterClassId, AbilityInfo>;
  for (const [id, info] of Object.entries(ABILITY_INFO) as Array<[CharacterClassId, AbilityInfo]>) {
    out[id] = { id: info.id, name: t(info.name), blurb: t(info.blurb) };
  }
  return out;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

/** Server text is English; known sentences translate through `t()`, the rest read as sent. */
function errorMessage(err: unknown): string {
  const message = err instanceof ApiError ? err.message : (err as Error)?.message;
  return message ? serverText(message) : t('Something went wrong');
}

export function openCharacterCreationWizard(onCreated: () => void | Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-50 scrim flex items-center justify-center p-4 overflow-y-auto';
  overlay.innerHTML = `
    <div class="frame-raised w-full max-w-[520px] my-4 p-5 flex flex-col gap-4 max-h-[92dvh] overflow-y-auto">
      <div class="flex items-center justify-between gap-3">
        <h2 class="font-heading text-gold text-lg tracking-wide">${t('Create Your Delver')}</h2>
        <button id="wiz-close" type="button" aria-label="${t('Close')}" class="btn btn-quiet min-w-11 px-0 justify-center">${iconMarkup('close')}</button>
      </div>
      <div id="wiz-steps" class="flex gap-1.5"></div>
      <div id="wiz-error" class="notice-error" hidden></div>
      <div id="wiz-body" class="flex flex-col gap-3 min-h-[240px]"></div>
      <div class="flex gap-3">
        <button id="wiz-back" type="button" class="btn btn-stone flex-1">${t('Back')}</button>
        <button id="wiz-next" type="button" class="btn btn-gold flex-1">${t('Next')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const $ = <T extends HTMLElement>(id: string) => overlay.querySelector(`#${id}`) as T;
  const stepsEl = $<HTMLDivElement>('wiz-steps');
  const errEl = $<HTMLDivElement>('wiz-error');
  const bodyEl = $<HTMLDivElement>('wiz-body');
  const backBtn = $<HTMLButtonElement>('wiz-back');
  const nextBtn = $<HTMLButtonElement>('wiz-next');
  const closeBtn = $<HTMLButtonElement>('wiz-close');

  let stepIndex = 0;
  let selectedClass: CharacterClassId | null = null;
  let selectedTalentNodeId: string | null = null;
  let name = '';
  let talentTree: TalentNodeRow[] = [];
  let submitting = false;

  function close(): void {
    overlay.remove();
  }

  function showError(message: string): void {
    errEl.textContent = message;
    errEl.hidden = false;
  }
  function hideError(): void {
    errEl.hidden = true;
  }

  function canAdvanceFrom(step: StepId): boolean {
    if (step === 'class') return selectedClass !== null;
    if (step === 'name') return name.trim().length > 0;
    return true; // abilities/stats are read-only steps; the talent pick is optional
  }

  function renderStepDots(): void {
    stepsEl.innerHTML = STEPS.map((_, i) => `
      <div class="h-1.5 flex-1 rounded-full ${i <= stepIndex ? 'bg-gold' : 'bg-border'}"></div>
    `).join('');
  }

  async function renderStep(): Promise<void> {
    hideError();
    renderStepDots();
    const step = STEPS[stepIndex];
    backBtn.disabled = stepIndex === 0 || submitting;
    nextBtn.textContent = step === 'name' ? t('Create Character') : t('Next');
    nextBtn.disabled = submitting || !canAdvanceFrom(step);

    if (step === 'class') {
      bodyEl.innerHTML = classStepMarkup(CHARACTER_ROSTER, selectedClass);
      bodyEl.querySelectorAll<HTMLButtonElement>('[data-pick-class]').forEach((btn) => {
        btn.addEventListener('click', () => {
          selectedClass = btn.dataset.pickClass as CharacterClassId;
          void renderStep();
        });
      });
      return;
    }

    if (step === 'abilities') {
      const mine = selectedClass!;
      const others = CHARACTER_ROSTER.map((c) => c.id).filter((id) => id !== mine);
      bodyEl.innerHTML = abilityStepMarkup(mine, others, abilityInfoTranslated());
      return;
    }

    if (step === 'stats') {
      const entry = CHARACTER_ROSTER.find((c) => c.id === selectedClass)!;
      bodyEl.innerHTML = `
        <p class="text-[13px] text-text-dim">${escapeHtml(t("A quick look at your class's identity — nothing here changes; talents grow these numbers later."))}</p>
        <div class="grid grid-cols-2 gap-2">
          <div class="frame-inset p-3">
            <div class="eyebrow eyebrow-dim">${escapeHtml(t('Weapon'))}</div>
            <div class="font-heading text-text text-sm mt-0.5 capitalize">${entry.weaponClass.toLowerCase()}</div>
          </div>
          <div class="frame-inset p-3">
            <div class="eyebrow eyebrow-dim">${escapeHtml(t('Base HP'))}</div>
            <div class="font-heading text-text text-sm mt-0.5">100</div>
          </div>
          <div class="frame-inset p-3">
            <div class="eyebrow eyebrow-dim">${escapeHtml(t('Base Stamina'))}</div>
            <div class="font-heading text-text text-sm mt-0.5">100</div>
          </div>
          <div class="frame-inset p-3">
            <div class="eyebrow eyebrow-dim">${escapeHtml(t('Fighting Style'))}</div>
            <div class="text-[12px] text-text mt-0.5">${escapeHtml(t(WEAPON_IDENTITY[entry.weaponClass]))}</div>
          </div>
        </div>
      `;
      return;
    }

    if (step === 'talent') {
      bodyEl.innerHTML = `<p class="text-[13px] text-text-dim">${escapeHtml(t('Loading talents…'))}</p>`;
      try {
        talentTree = await getTalentTree(selectedClass!);
      } catch (err) {
        showError(errorMessage(err));
        talentTree = [];
      }
      // talent-trees.md Core Rule #1 — tier 2/3 need points already spent in
      // the tree, so at creation (0 spent) only tier-1 nodes are choosable.
      const tier1 = talentTree.filter((n) => n.tier === 1);
      bodyEl.innerHTML = `
        <p class="text-[13px] text-text-dim">${escapeHtml(t('Spend your first talent point now, or save it for the hub later.'))}</p>
        <div class="flex flex-col gap-2">
          ${tier1.map((n) => talentOptionMarkup(n, selectedClass!, selectedTalentNodeId === n.id)).join('')}
        </div>
        <button type="button" id="wiz-talent-skip" class="min-h-11 px-3 rounded border border-border text-[13px] text-text-dim hover:text-text hover:border-gold/50">
          ${escapeHtml(t('Save this point for later'))}
        </button>
      `;
      // 52 of the 108 nodes ship ahead of their art, and a tier-1 node is the
      // first talent picture a new player ever sees.
      wireIconFallbacks(bodyEl);
      bodyEl.querySelectorAll<HTMLButtonElement>('[data-pick-talent]').forEach((btn) => {
        btn.addEventListener('click', () => {
          selectedTalentNodeId = selectedTalentNodeId === btn.dataset.pickTalent ? null : btn.dataset.pickTalent!;
          void renderStep();
        });
      });
      // Banking is the whole action, so it moves on: clearing the pick and
      // re-rendering the same step looked like a dead button (QA 2026-10-15).
      // The character is created with its one point unspent either way
      // (character-creation.md), and the hub's talent tree spends it later.
      $<HTMLButtonElement>('wiz-talent-skip').addEventListener('click', () => {
        selectedTalentNodeId = null;
        stepIndex += 1;
        void renderStep();
      });
      return;
    }

    // step === 'name'
    bodyEl.innerHTML = `
      <p class="text-[13px] text-text-dim">${escapeHtml(t('Name your delver. This is permanent and must be unique.'))}</p>
      <input id="wiz-name-input" type="text" maxlength="20" placeholder="${escapeHtml(t('Character name'))}" value="${escapeHtml(name)}"
        class="min-h-12 px-3 bg-surface-2 text-text border border-border rounded outline-none focus:border-gold" />
      <div class="p-3 rounded border border-border bg-surface-2 text-[12px] text-text-dim flex flex-col gap-1">
        <div>${t('Class: {name}', { name: `<span class="text-text capitalize">${escapeHtml(classDisplayName(String(selectedClass)))}</span>` })}</div>
        <div>${t('Talent: {name}', { name: `<span class="text-text">${escapeHtml(selectedTalentNodeId ? talentCopy(selectedTalentNodeId).name : t('1 point saved for the hub'))}</span>` })}</div>
      </div>
    `;
    const nameInput = $<HTMLInputElement>('wiz-name-input');
    nameInput.addEventListener('input', () => {
      name = nameInput.value;
      nextBtn.disabled = !canAdvanceFrom('name') || submitting;
    });
    nameInput.focus();
  }

  async function submit(): Promise<void> {
    submitting = true;
    hideError();
    nextBtn.disabled = true;
    backBtn.disabled = true;
    nextBtn.textContent = t('Creating…');
    try {
      const character = await createCharacter(name.trim(), selectedClass!);
      if (selectedTalentNodeId) {
        await allocateTalent(character.id, selectedTalentNodeId);
      }
      await onCreated();
      close();
    } catch (err) {
      showError(errorMessage(err));
      submitting = false;
      nextBtn.disabled = false;
      backBtn.disabled = false;
      nextBtn.textContent = t('Create Character');
    }
  }

  backBtn.addEventListener('click', () => {
    if (stepIndex === 0 || submitting) return;
    stepIndex -= 1;
    void renderStep();
  });
  nextBtn.addEventListener('click', () => {
    const step = STEPS[stepIndex];
    if (!canAdvanceFrom(step)) return;
    if (step === 'name') {
      void submit();
      return;
    }
    stepIndex += 1;
    void renderStep();
  });
  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  void renderStep();
}
